"use client";

import { useEffect, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import { ProductCatalog, type ProductItem } from "@/components/ProductCatalog";
import { FastenerCalculator, SingleCalculator } from "@/components/FastenerCalculator";
import { RfqForm } from "@/components/RfqForm";
import { translateHtmlText, translateText, translateElement } from "@/lib/i18n-dict";
import { isValidCalculatorId } from "@/lib/calculator-shared";
import { extractLayoutRegions, scopeRenderedPageCss, stripLayoutCss } from "@/lib/grapesjs-layout-plugin";
import { renderSocialLinksHtml, type DynamicSettings as SocialDynamicSettings } from "@/lib/social-render";
import { calculatorPluginPlaceholderHtml } from "@/lib/plugins/calculator/plugin";

type VisualPageData = {
  html: string;
  css: string;
  schemaVersion?: number;
};

type KnowledgeArticle = {
  title: string;
  category: string;
  slug: string;
  summary: string;
  contentMd?: string;
  sourceUrl?: string;
  tags?: string[];
};

type DynamicSettings = SocialDynamicSettings & {
  mapEmbedUrl?: string;
  amapEmbedUrl?: string;
  baiduMapEmbedUrl?: string;
  tencentMapEmbedUrl?: string;
  embedCode?: string;
};

const FRONTEND_BUILDER_MODULE_OVERRIDES = `
.grapes-rendered-page .st-text-cover {
  display: grid !important;
  grid-template-columns: minmax(0, .78fr) minmax(360px, 1fr) !important;
  align-items: stretch !important;
  width: 100% !important;
  min-height: 420px !important;
  overflow: hidden !important;
  background: #102033 !important;
  color: #fff !important;
}
.grapes-rendered-page .st-text-cover-copy {
  display: flex !important;
  flex-direction: column !important;
  justify-content: center !important;
  min-width: 0 !important;
  padding: 64px max(28px, calc((100vw - 1180px) / 2 + 28px)) !important;
}
.grapes-rendered-page .st-text-cover-media {
  position: relative !important;
  align-self: stretch !important;
  min-height: 360px !important;
  overflow: hidden !important;
  background-position: center !important;
  background-repeat: no-repeat !important;
  background-size: cover !important;
}
.grapes-rendered-page .st-text-cover-media img,
.grapes-rendered-page .st-text-cover-media video,
.grapes-rendered-page .st-text-cover-media iframe {
  display: block !important;
  width: 100% !important;
  height: 100% !important;
  min-height: 360px !important;
  border: 0 !important;
  object-fit: cover !important;
}
.grapes-rendered-page .st-text-cover h1 {
  margin: 16px 0 !important;
  color: #fff !important;
  font-size: clamp(2.75rem, 5vw, 4.375rem) !important;
  font-weight: 500 !important;
  line-height: .98 !important;
}
.grapes-rendered-page .st-text-cover p {
  color: #dbe8f6 !important;
  line-height: 1.75 !important;
}
.grapes-rendered-page .rt-section.base-media-row {
  display: grid !important;
  grid-template-columns: minmax(0, .92fr) minmax(320px, 1fr) !important;
  gap: 42px !important;
  align-items: center !important;
  max-width: 1180px !important;
  margin-right: auto !important;
  margin-left: auto !important;
  padding-right: 28px !important;
  padding-left: 28px !important;
}
.grapes-rendered-page .rt-section.base-media-row > * {
  min-width: 0 !important;
}
.grapes-rendered-page .rt-section.base-media-row img,
.grapes-rendered-page .rt-section.base-media-row video,
.grapes-rendered-page .rt-section.base-media-row iframe {
  display: block !important;
  width: 100% !important;
  max-width: 100% !important;
  aspect-ratio: 4 / 3 !important;
  border: 0 !important;
  border-radius: 8px !important;
  object-fit: cover !important;
}
.grapes-rendered-page .st-columns {
  display: grid !important;
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  gap: 18px !important;
  align-items: stretch !important;
}
.grapes-rendered-page .st-columns article {
  display: flex !important;
  min-width: 0 !important;
  flex-direction: column !important;
}
.grapes-rendered-page .st-columns img {
  width: 100% !important;
  aspect-ratio: 4 / 3 !important;
  object-fit: cover !important;
}
.grapes-rendered-page .base-stats,
.grapes-rendered-page .rt-milestone-list {
  display: grid !important;
  grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
}
.grapes-rendered-page .base-stats {
  gap: 1px !important;
  overflow: hidden !important;
  border: 1px solid var(--rt-line, #dbe5ef) !important;
  border-radius: var(--rt-radius, 8px) !important;
  background: var(--rt-line, #dbe5ef) !important;
}
.grapes-rendered-page .rt-milestone-list {
  gap: 18px !important;
}
.grapes-rendered-page .base-stats article,
.grapes-rendered-page .base-stats div {
  background: #fff !important;
  padding: 30px 22px !important;
  text-align: center !important;
}
.grapes-rendered-page .base-stats strong {
  display: block !important;
  margin-bottom: 10px !important;
  color: var(--rt-primary, #1b5f9f) !important;
  font-size: 34px !important;
  font-weight: 900 !important;
  line-height: 1 !important;
}
.grapes-rendered-page .base-stats span {
  color: var(--rt-muted, #64728a) !important;
  font-weight: 700 !important;
}
.grapes-rendered-page .st-media-list {
  display: grid !important;
  gap: 14px !important;
}
.grapes-rendered-page .st-media-list article {
  display: grid !important;
  grid-template-columns: 180px minmax(0, 1fr) !important;
  gap: 20px !important;
  align-items: center !important;
}
.grapes-rendered-page .st-media-list img,
.grapes-rendered-page .st-media-list video,
.grapes-rendered-page .st-media-list iframe,
.grapes-rendered-page .st-media-thumb,
.grapes-rendered-page .st-media-video {
  display: block !important;
  width: 100% !important;
  aspect-ratio: 4 / 3 !important;
  border: 0 !important;
  border-radius: 6px !important;
  object-fit: cover !important;
  background: #102033 !important;
}
.grapes-rendered-page .st-media-showcase {
  box-sizing: border-box !important;
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
  overflow: hidden !important;
  padding: clamp(56px, 7vw, 88px) max(28px, calc((100vw - 1180px) / 2 + 28px)) !important;
  background: #f4f8fc !important;
  color: #1a2233 !important;
}
.grapes-rendered-page .st-media-showcase > .rt-section-head,
.grapes-rendered-page .st-media-showcase > .st-media-showcase-head {
  max-width: 760px !important;
  margin: 0 auto 34px !important;
  text-align: center !important;
}
.grapes-rendered-page .st-media-showcase h2 {
  margin: 14px 0 12px !important;
  color: var(--rt-ink, #1a2233) !important;
  font-size: clamp(2.1rem, 4vw, 3.4rem) !important;
  font-weight: 700 !important;
  line-height: 1.08 !important;
}
.grapes-rendered-page .st-media-showcase p {
  color: var(--rt-muted, #64748b) !important;
  font-size: 1.02rem !important;
  line-height: 1.75 !important;
}
.grapes-rendered-page .st-media-showcase-grid {
  display: grid !important;
  grid-template-columns: minmax(280px, .78fr) minmax(360px, 1.22fr) !important;
  gap: clamp(24px, 4vw, 48px) !important;
  align-items: stretch !important;
  width: min(1180px, 100%) !important;
  margin: 0 auto !important;
}
.grapes-rendered-page .st-media-showcase-stack {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  grid-auto-rows: minmax(180px, auto) !important;
  gap: 16px !important;
}
.grapes-rendered-page .st-media-showcase-card {
  position: relative !important;
  overflow: hidden !important;
  border: 1px solid #dbe5ef !important;
  border-radius: 10px !important;
  background: #fff !important;
}
.grapes-rendered-page .st-media-showcase-card.is-tall {
  grid-row: span 2 !important;
}
.grapes-rendered-page .st-media-showcase-card img,
.grapes-rendered-page .st-media-showcase-card video,
.grapes-rendered-page .st-media-showcase-card iframe {
  display: block !important;
  width: 100% !important;
  height: 100% !important;
  min-height: 220px !important;
  object-fit: cover !important;
}
.grapes-rendered-page .st-media-showcase-card figcaption,
.grapes-rendered-page .st-media-showcase-caption {
  position: absolute !important;
  right: 12px !important;
  bottom: 12px !important;
  left: 12px !important;
  padding: 10px 12px !important;
  border-radius: 8px !important;
  background: rgba(12, 32, 52, .78) !important;
  color: #fff !important;
  font-size: .92rem !important;
  font-weight: 700 !important;
  line-height: 1.35 !important;
}
.grapes-rendered-page .st-images-wall img,
.grapes-rendered-page .st-picture img {
  display: block !important;
  width: 100% !important;
  object-fit: cover !important;
}
@media (max-width: 980px) {
  .grapes-rendered-page .st-columns,
  .grapes-rendered-page .base-stats,
  .grapes-rendered-page .rt-milestone-list {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
  .grapes-rendered-page .st-media-showcase-grid {
    grid-template-columns: 1fr !important;
  }
}
@media (max-width: 820px) {
  .grapes-rendered-page .st-text-cover,
  .grapes-rendered-page .rt-section.base-media-row,
  .grapes-rendered-page .st-media-list article {
    grid-template-columns: 1fr !important;
  }
  .grapes-rendered-page .st-text-cover-copy {
    padding: 48px 24px !important;
  }
}
@media (max-width: 640px) {
  .grapes-rendered-page .st-columns,
  .grapes-rendered-page .base-stats,
  .grapes-rendered-page .rt-milestone-list,
  .grapes-rendered-page .st-media-showcase-stack {
    grid-template-columns: 1fr !important;
  }
  .grapes-rendered-page .st-media-showcase-card.is-tall {
    grid-row: auto !important;
  }
}
`;

const BUILDER_BLOCK_CSS_HREF = "/assets/builder-blocks/index.css?v=5";

export function VisualPageFrontend({ page }: { page: string }) {
  const [visualPage, setVisualPage] = useState<VisualPageData | null>(null);
  const [loaded, setLoaded] = useState(false);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const lang: "zh" | "en" =
    typeof window !== "undefined" &&
      (window.location.pathname.startsWith("/en/") || window.location.pathname === "/en")
      ? "en"
      : "zh";

  useEffect(() => {
    async function loadVisualPage() {
      clearLegacyVisualPageCache();

      try {
        const response = await fetch(`/api/visual-pages?page=${encodeURIComponent(page)}`, { cache: "no-store" });
        const payload = await response.json() as { data?: VisualPageData | null };
        if (payload.data?.schemaVersion === 2) {
          const { contentHtml } = extractLayoutRegions(payload.data.html ?? "");
          const htmlOut = stripBuilderAttributes(translateHtmlText(contentHtml, lang));
          setVisualPage({ html: htmlOut, css: scopeRenderedPageCss(stripLayoutCss(payload.data.css ?? "")) });
        }
      } catch {
        setVisualPage(null);
      } finally {
        setLoaded(true);
      }
    }

    void loadVisualPage();
  }, [page, lang]);

  useEffect(() => {
    if (!loaded || !pageRef.current) return;
    const root = pageRef.current;
    let cancelled = false;
    (async () => {
      await Promise.all([
        hydrateKnowledgeBlocks(root, lang),
        hydrateProductBlocks(root, lang),
        hydrateDynamicSettingsBlocks(root, lang),
        hydrateRfqBlocks(root, lang),
      ]);
      if (cancelled) return;
      hydrateCalculatorBlocks(root);
      normalizeSavedMediaBlocks(root);
      // 对 CMS 页面中因 dangerouslySetInnerHTML / 动态水合后仍残留的中文文本做 DOM 级翻译
      // （典型情况：翻译表未命中的段落、placeholder / alt / title 属性）
      if (lang === "en") translateElement(root, "en");
    })();
    return () => { cancelled = true; };
  }, [loaded, visualPage, lang]);

  if (!loaded) {
    return <section className="visual-page-empty" />;
  }

    if (!visualPage) {
      const isProductPage = page === "/products" || page === "/en/products";
      const isCalculatorPage = page === "/calculator" || page === "/en/calculator";
      const isRfqPage = page === "/rfq" || page === "/en/rfq";
      const calculatorDetailMatch = page.match(/^\/(?:en\/)?calculator\/([^/]+)$/);
    const calculatorDetailId = calculatorDetailMatch ? decodeURIComponent(calculatorDetailMatch[1] ?? "") : "";
    if (isProductPage) {
      const eyebrow = lang === "en" ? "PRODUCTS" : translateText("产品中心", lang);
      const h2 = translateText("产品展示", lang);
      const sub = translateText(
        "数据实时同步自库存管理系统，涵盖标准紧固件、定制件和全品类库存物料。",
        lang,
      );
      const allTag = translateText("全部", lang);
      const washerTag = translateText("垫圈", lang);
      const nutTag = translateText("螺母", lang);
      const screwBoltTag = lang === "en" ? "screw/bolt" : translateText("螺丝/螺栓", lang);
      return (
        <section className="grapes-rendered-page">
          <div ref={pageRef}>
            <section className="rt-section">
              <div className="rt-section-head">
                <span className="rt-eyebrow">{eyebrow}</span>
                <h2>{h2}</h2>
                <p>{sub}</p>
              </div>
              <div className="rt-product-categories">
                <span>{washerTag}</span>
                <span>{nutTag}</span>
                <span>{screwBoltTag}</span>
              </div>
              <div data-rt-dynamic="product-list" data-rt-product-layout="category" data-rt-grid-page-size="64" data-rt-list-page-size="20" data-rt-compact-page-size="20" />
              <div className="rt-product-categories" style={{ marginTop: 16 }} hidden>
                <span>{allTag}</span>
              </div>
            </section>
          </div>
        </section>
      );
      }

    if (isRfqPage) {
      return (
        <section className="grapes-rendered-page">
          <div ref={pageRef}>
            <section className="rt-section">
              <div data-rt-dynamic="rfq-form" />
            </section>
          </div>
        </section>
      );
    }

    if (isCalculatorPage) {
      const isEn = page === "/en/calculator";
      return (
        <section className="grapes-rendered-page">
          <div ref={pageRef} dangerouslySetInnerHTML={{ __html: isEn ? translateHtmlText(calculatorPluginPlaceholderHtml(), "en") : calculatorPluginPlaceholderHtml() }} />
        </section>
      );
    }

    if (calculatorDetailId && isValidCalculatorId(calculatorDetailId)) {
      return (
        <section className="grapes-rendered-page">
          <div ref={pageRef}>
            <SingleCalculator id={calculatorDetailId} lang={lang} />
          </div>
        </section>
      );
    }

    const eyebrow = translateText("可视化页面", lang);
    const h1 = translateText("这个页面已经切换到新版前端。", lang);
    const sub = translateText("请进入 Web Builder 添加模块并保存。", lang);
    const btn = translateText("打开编辑器", lang);
    return (
      <section className="visual-page-empty">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h1>{h1}</h1>
          <p>{sub}</p>
          <a className="button primary" href={`/builder?path=${encodeURIComponent(page)}`}>{btn}</a>
        </div>
      </section>
    );
  }

  return (
    <section className="grapes-rendered-page">
      <link rel="stylesheet" href={BUILDER_BLOCK_CSS_HREF} />
      {visualPage.css && <style dangerouslySetInnerHTML={{ __html: visualPage.css }} />}
      <style dangerouslySetInnerHTML={{ __html: FRONTEND_BUILDER_MODULE_OVERRIDES }} />
      <div ref={pageRef} dangerouslySetInnerHTML={{ __html: visualPage.html }} />
    </section>
  );
}

function hydrateRfqBlocks(root: HTMLElement, lang: "zh" | "en") {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='rfq-form']"));
  blocks.forEach((block) => {
    block.innerHTML = "";
    const reactRoot = createRoot(block);
    reactRoot.render(
      <RfqForm
        lang={lang}
        title={block.dataset.rtRfqTitle}
        intro={block.dataset.rtRfqIntro}
        submitLabel={block.dataset.rtRfqSubmit}
      />,
    );
    productRoots.push(reactRoot);
  });
}

async function hydrateDynamicSettingsBlocks(root: HTMLElement, lang: "zh" | "en") {
  const mapBlocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='map'], .db-map"));
  const socialBlocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='social-links'], .db-social"));
  const embedBlocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='embed-code'], .db-embed"));
  const searchBlocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='search-box']"));
  const newsletterBlocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='newsletter-form']"));
  const popupBlocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='popup-box']"));
  const countdownBlocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='countdown']"));
  const discussionBlocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='discussion-form']"));
  if (
    !mapBlocks.length &&
    !socialBlocks.length &&
    !embedBlocks.length &&
    !searchBlocks.length &&
    !newsletterBlocks.length &&
    !popupBlocks.length &&
    !countdownBlocks.length &&
    !discussionBlocks.length
  ) return;

  const response = await fetch("/api/dynamic-settings", { cache: "no-store" });
  const payload = await response.json() as { data?: DynamicSettings };
  const settings = payload.data ?? {};

  mapBlocks.forEach((block) => {
    block.innerHTML = renderMapBlock(settings, lang);
  });
  socialBlocks.forEach((block) => {
    block.innerHTML = renderSocialLinksHtml(settings, lang);
  });
  embedBlocks.forEach((block) => {
    block.innerHTML = renderEmbedBlock(settings, lang);
  });
  searchBlocks.forEach((block) => {
    block.innerHTML = renderSearchBlock(block, lang);
  });
  newsletterBlocks.forEach((block) => {
    block.innerHTML = renderNewsletterBlock(block, lang);
  });
  popupBlocks.forEach((block) => {
    block.innerHTML = renderPopupBlock(block, lang);
  });
  countdownBlocks.forEach((block) => {
    block.innerHTML = renderCountdownBlock(block, lang);
  });
  discussionBlocks.forEach((block) => {
    block.innerHTML = renderDiscussionBlock(block, lang);
  });
}

function renderMapBlock(settings: DynamicSettings, lang: "zh" | "en") {
  const mapUrl =
    settings.amapEmbedUrl ||
    settings.baiduMapEmbedUrl ||
    settings.tencentMapEmbedUrl ||
    settings.mapEmbedUrl ||
    "";
  const title = lang === "en" ? "Map" : "地图";
  const empty = lang === "en"
    ? "Map embed URL has not been configured in CMS."
    : "请先在内容后台配置高德、百度、腾讯或 Google 地图嵌入 URL。";

  if (!isSafeEmbedUrl(mapUrl)) {
    return `<div class="rt-dynamic-empty">${escapeHtml(empty)}</div>`;
  }

  return `<iframe class="rt-dynamic-map-frame" src="${escapeAttribute(mapUrl)}" title="${escapeAttribute(title)}" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>`;
}

function renderEmbedBlock(settings: DynamicSettings, lang: "zh" | "en") {
  const code = settings.embedCode?.trim() ?? "";
  if (!code) {
    const empty = lang === "en"
      ? "Embed code has not been configured in CMS."
      : "请先在内容后台配置嵌入代码。";
    return `<div class="rt-dynamic-empty">${escapeHtml(empty)}</div>`;
  }

  if (!isSafeEmbedCode(code)) {
    const blocked = lang === "en"
      ? "Embed code contains unsupported tags and was not rendered."
      : "嵌入代码包含暂不支持的标签，已停止渲染。";
    return `<div class="rt-dynamic-empty">${escapeHtml(blocked)}</div>`;
  }

  return code;
}

function renderSearchBlock(block: HTMLElement, lang: "zh" | "en") {
  const placeholder = block.dataset.rtSearchPlaceholder || (lang === "en" ? "Search products, standards, articles or downloads" : "搜索产品、标准、文章或资料");
  const button = block.dataset.rtSearchButton || (lang === "en" ? "Search" : "搜索");
  const rawAction = block.dataset.rtSearchAction || (lang === "en" ? "/en/products" : "/products");
  const action = isSafeLinkUrl(rawAction) ? rawAction : "/products";
  return `
    <form class="db-search rt-dynamic-search" action="${escapeAttribute(action)}" method="get">
      <input type="search" name="q" placeholder="${escapeAttribute(translateText(placeholder, lang))}" />
      <button type="submit">${escapeHtml(translateText(button, lang))}</button>
    </form>
  `;
}

function renderNewsletterBlock(block: HTMLElement, lang: "zh" | "en") {
  const title = block.dataset.rtNewsletterTitle || (lang === "en" ? "Subscribe to updates" : "订阅资料更新");
  const intro = block.dataset.rtNewsletterIntro || (lang === "en" ? "Receive product, resource and technical article updates." : "接收产品、资料和技术文章更新。");
  const placeholder = block.dataset.rtNewsletterPlaceholder || (lang === "en" ? "Email address" : "输入邮箱");
  const button = block.dataset.rtNewsletterButton || (lang === "en" ? "Subscribe" : "订阅");
  return `
    <div class="rt-dynamic-newsletter">
      <div><h2>${escapeHtml(translateText(title, lang))}</h2><p>${escapeHtml(translateText(intro, lang))}</p></div>
      <form class="ib-newsletter" method="post" action="/api/newsletter">
        <input type="email" name="email" placeholder="${escapeAttribute(translateText(placeholder, lang))}" required />
        <button type="submit">${escapeHtml(translateText(button, lang))}</button>
      </form>
    </div>
  `;
}

function renderPopupBlock(block: HTMLElement, lang: "zh" | "en") {
  const title = block.dataset.rtPopupTitle || (lang === "en" ? "Notice" : "重要提示");
  const text = block.dataset.rtPopupText || (lang === "en" ? "Edit this popup content in the builder." : "在编辑器中修改弹窗内容。");
  const button = block.dataset.rtPopupButton || (lang === "en" ? "Learn more" : "了解更多");
  const rawLink = block.dataset.rtPopupLink || "/rfq";
  const link = isSafeLinkUrl(rawLink) ? rawLink : "/rfq";
  return `
    <div class="db-popup rt-dynamic-popup">
      <div>
        <button type="button" aria-label="${lang === "en" ? "Close" : "关闭"}">×</button>
        <h3>${escapeHtml(translateText(title, lang))}</h3>
        <p>${escapeHtml(translateText(text, lang))}</p>
        <a class="rt-button primary" href="${escapeAttribute(link)}">${escapeHtml(translateText(button, lang))}</a>
      </div>
    </div>
  `;
}

function renderCountdownBlock(block: HTMLElement, lang: "zh" | "en") {
  const title = block.dataset.rtCountdownTitle || (lang === "en" ? "Countdown" : "项目倒计时");
  const target = block.dataset.rtCountdownTarget || "2026-12-31";
  const targetDate = new Date(`${target}T00:00:00`);
  const diffMs = Number.isFinite(targetDate.getTime()) ? Math.max(0, targetDate.getTime() - Date.now()) : 0;
  const days = Math.floor(diffMs / 86400000);
  const hours = Math.floor((diffMs % 86400000) / 3600000);
  const minutes = Math.floor((diffMs % 3600000) / 60000);
  return `
    <div class="rt-dynamic-countdown">
      <h3>${escapeHtml(translateText(title, lang))}</h3>
      <div class="db-countdown">
        <div><strong>${days}</strong><span>${lang === "en" ? "Days" : "天"}</span></div>
        <div><strong>${hours}</strong><span>${lang === "en" ? "Hours" : "小时"}</span></div>
        <div><strong>${minutes}</strong><span>${lang === "en" ? "Minutes" : "分钟"}</span></div>
      </div>
    </div>
  `;
}

function renderDiscussionBlock(block: HTMLElement, lang: "zh" | "en") {
  const title = block.dataset.rtDiscussionTitle || (lang === "en" ? "Message" : "需求留言");
  const placeholder = block.dataset.rtDiscussionPlaceholder || (lang === "en" ? "Describe your requirement..." : "写下您的需求...");
  const button = block.dataset.rtDiscussionButton || (lang === "en" ? "Submit" : "提交留言");
  return `
    <form class="db-discussion rt-dynamic-discussion" method="post" action="/api/rfq">
      <h3>${escapeHtml(translateText(title, lang))}</h3>
      <textarea name="message" placeholder="${escapeAttribute(translateText(placeholder, lang))}" required></textarea>
      <button type="submit">${escapeHtml(translateText(button, lang))}</button>
    </form>
  `;
}

function stripBuilderAttributes(html: string) {
  return html
    .replace(/\sdata-builder-region="[^"]*"/g, "")
    .replace(/\sdata-builder-field="[^"]*"/g, "");
}

function normalizeSavedMediaBlocks(root: HTMLElement) {
  const videoSources = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-video-src]"));
  videoSources.forEach((target) => {
    const rawSrc = target.dataset.rtVideoSrc || target.getAttribute("src") || "";
    const src = normalizeVideoSrc(rawSrc);
    if (!src) return;

    if (target.tagName.toLowerCase() === "video") {
      normalizeVideoElement(target as HTMLVideoElement, src);
      return;
    }

    const existingVideo = target.querySelector<HTMLVideoElement>("video");
    if (existingVideo) {
      normalizeVideoElement(existingVideo, src);
      return;
    }

    const video = document.createElement("video");
    video.className = "st-media-video";
    video.textContent = "当前浏览器不支持视频播放。";
    normalizeVideoElement(video, src);
    target.replaceWith(video);
  });

  root.querySelectorAll<HTMLVideoElement>("video").forEach((video) => {
    const src = normalizeVideoSrc(
      video.getAttribute("src") ||
      video.dataset.rtVideoSrc ||
      video.querySelector("source")?.getAttribute("src") ||
      "",
    );
    if (src) normalizeVideoElement(video, src);
  });
}

function normalizeVideoSrc(value: string) {
  const clean = value.trim();
  if (!clean) return "";
  if (clean.startsWith("/") || /^https?:\/\//i.test(clean) || /^data:video\//i.test(clean)) return clean;
  return "";
}

function normalizeVideoElement(video: HTMLVideoElement, src: string) {
  video.setAttribute("src", src);
  video.setAttribute("data-rt-video", "true");
  video.setAttribute("data-rt-video-src", src);
  video.setAttribute("controls", "");
  video.setAttribute("playsinline", "");
  video.setAttribute("preload", video.getAttribute("preload") || "metadata");
  if (!video.getAttribute("poster")) video.setAttribute("poster", "/ccd-sorting-workshop.jpg");
  video.classList.add("st-media-video");
}

function clearLegacyVisualPageCache() {
  try {
    const keys = Object.keys(window.localStorage ?? {});
    keys.forEach((key) => {
      if (key.startsWith("ronnietech-visual-page:")) window.localStorage.removeItem(key);
    });
  } catch {
    // Cache cleanup is best-effort only.
  }
}

async function hydrateKnowledgeBlocks(root: HTMLElement, lang: "zh" | "en") {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='knowledge-list']"));
  if (!blocks.length) return;

  const response = await fetch("/api/knowledge", { cache: "no-store" });
  const payload = await response.json() as { data?: KnowledgeArticle[] };
  const articles = Array.isArray(payload.data) ? payload.data : [];

  blocks.forEach((block) => {
    block.innerHTML = renderKnowledgeList(articles, lang);
  });
}

function renderKnowledgeList(articles: KnowledgeArticle[], lang: "zh" | "en") {
  if (!articles.length) {
    return `<div class="rt-dynamic-empty">${translateText("暂无知识文章，请先在后台内容管理中添加。", lang)}</div>`;
  }

  const detailBase = lang === "en" ? "/en/knowledge" : "/knowledge";
  const readLabel = lang === "en" ? "Read" : "查看";

  return `
    <div class="rt-knowledge-list">
      ${articles.map((article) => {
        const href = `${detailBase}/${encodeURIComponent(article.slug)}`;
        const summary = article.summary || article.contentMd || "";
        const tags = Array.isArray(article.tags) ? article.tags.slice(0, 4) : [];

        return `
          <article class="rt-knowledge-row">
            <div class="rt-knowledge-meta">
              <span>${escapeHtml(translateText(article.category || "知识文章", lang))}</span>
              ${tags.length ? `<div class="rt-tags">${tags.map((tag) => `<span>${escapeHtml(translateText(tag, lang))}</span>`).join("")}</div>` : ""}
            </div>
            <div class="rt-knowledge-main">
              <h3><a href="${href}">${escapeHtml(translateText(article.title, lang))}</a></h3>
              <p>${escapeHtml(translateText(summary, lang))}</p>
            </div>
            <a class="rt-knowledge-link" href="${href}">${readLabel}</a>
          </article>
        `;
      }).join("")}
    </div>
  `;
}

const productRoots: Root[] = [];

async function hydrateProductBlocks(root: HTMLElement, lang: "zh" | "en") {
  const blocks = Array.from(root.querySelectorAll<HTMLElement>("[data-rt-dynamic='product-list']"));
  if (!blocks.length) return;

  productRoots.forEach((r) => r.unmount());
  productRoots.length = 0;

  blocks.forEach((block) => {
    block.innerHTML = `<div class="rt-dynamic-empty">${lang === "en" ? "Loading inventory products..." : "正在加载库存产品..."}</div>`;
  });

  let products: ProductItem[] = [];
  let categories: string[] = [];
  let settings: Record<string, unknown> = {};

  try {
    // 并行拉取产品数据和动态设置（含字段显示开关）
    const [productsResponse, settingsResponse] = await Promise.all([
      fetch("/api/products", { cache: "no-store" }),
      fetch("/api/dynamic-settings", { cache: "no-store" }),
    ]);
    if (!productsResponse.ok) throw new Error("products api failed");
    const payload = await productsResponse.json() as { data?: ProductItem[]; categories?: string[] };
    products = Array.isArray(payload.data) ? payload.data : [];
    categories = Array.isArray(payload.categories) ? payload.categories : [];
    const settingsPayload = settingsResponse.ok
      ? await settingsResponse.json() as { data?: Record<string, unknown> }
      : { data: {} };
    settings = settingsPayload.data ?? {};
  } catch {
    blocks.forEach((block) => {
      block.innerHTML = `<div class="rt-dynamic-empty">${lang === "en" ? "Inventory products failed to load. Please refresh the page." : "库存产品加载失败，请刷新页面。"}</div>`;
    });
    return;
  }

  blocks.forEach((block) => {
    // 从自身读取属性；若内层容器未设置，则从外层 Block 根（带 data-rt-block=product-list）继承
    const blockRoot = block.closest<HTMLElement>("[data-rt-block='product-list']");
    const mergedDataset = { ...(blockRoot?.dataset ?? {}), ...block.dataset };

    const raw = mergedDataset.rtProductLayout || "grid";
    const layout: "grid" | "list" | "compact" | "category" =
      raw === "list" || raw === "compact" || raw === "category" ? raw : "grid";
    // 读取模块属性中的每页数量配置
    const pageSizes: Partial<Record<"grid" | "list" | "compact", number>> = {};
    const gridPageSize = Number(mergedDataset.rtGridPageSize);
    if (Number.isFinite(gridPageSize) && gridPageSize > 0) pageSizes.grid = gridPageSize;
    const listPageSize = Number(mergedDataset.rtListPageSize);
    if (Number.isFinite(listPageSize) && listPageSize > 0) pageSizes.list = listPageSize;
    const compactPageSize = Number(mergedDataset.rtCompactPageSize);
    if (Number.isFinite(compactPageSize) && compactPageSize > 0) pageSizes.compact = compactPageSize;
    // 读取网格列数（2~8，默认 3）
    const gridColsRaw = Number(mergedDataset.rtGridCols);
    const gridCols =
      Number.isFinite(gridColsRaw) && gridColsRaw >= 2 && gridColsRaw <= 8
        ? Math.round(gridColsRaw)
        : 3;

    const reactRoot = createRoot(block);
    productRoots.push(reactRoot);
    reactRoot.render(
      <ProductCatalog
        products={products}
        categories={categories}
        defaultLayout={layout}
        pageSizes={pageSizes}
        gridCols={gridCols}
        lang={lang}
        fieldVisibility={{
          image: settings.productShowImage !== false,
          category: settings.productShowCategory !== false,
          standard: settings.productShowStandard !== false,
          material: settings.productShowMaterial !== false,
          finish: settings.productShowFinish !== false,
          stock: settings.productShowStock !== false,
          model: settings.productShowModel !== false,
          cartButton: settings.productShowCartButton !== false,
        }}
      />,
    );
  });
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function escapeAttribute(value: string) {
  return escapeHtml(value).replaceAll("'", "&#39;");
}

function isSafeEmbedUrl(value: string) {
  if (!value) return false;
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

function isSafeLinkUrl(value: string) {
  if (!value) return false;
  if (value.startsWith("/") || value.startsWith("#")) return true;
  try {
    const url = new URL(value);
    return ["https:", "http:", "mailto:", "tel:"].includes(url.protocol);
  } catch {
    return false;
  }
}

function isSafeImageSrc(value: string) {
  return value.startsWith("/") || value.startsWith("data:image/") || isSafeEmbedUrl(value);
}

function isSafeEmbedCode(value: string) {
  return !/<\s*(script|style|object|embed|link|meta)\b/i.test(value);
}

const calculatorRoots: Root[] = [];

function hydrateCalculatorBlocks(root: HTMLElement) {
  const blocks = Array.from(new Set(root.querySelectorAll<HTMLElement>(
    "[data-rt-dynamic='fastener-calculator'], [data-plugin-id='engineering-calculator'], .calculator-plugin-placeholder",
  )));
  if (!blocks.length) return;

  calculatorRoots.forEach((r) => r.unmount());
  calculatorRoots.length = 0;

  const lang: "zh" | "en" = window.location.pathname.startsWith("/en/") || window.location.pathname === "/en"
    ? "en"
    : "zh";

  blocks.forEach((block) => {
    block.classList.add("calculator-plugin-rendered");
    const reactRoot = createRoot(block);
    calculatorRoots.push(reactRoot);
    reactRoot.render(<FastenerCalculator lang={lang} />);
  });
}
